import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Upload, Check, X } from "lucide-react";
import { Button } from "./kit";
import api, { mediaUrl } from "../lib/api";
import { toast } from "sonner";

async function getCroppedBlob(src, crop) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(crop.width));
  canvas.height = Math.max(1, Math.round(crop.height));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/png", 0.95));
}

export function LogoUploader({ value, accent = "#106958", onUploaded }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(4);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5000000) { toast.error("Please use an image under 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setSrc(reader.result); setZoom(1); setCrop({ x: 0, y: 0 }); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const onCropComplete = useCallback((_, px) => setPixels(px), []);
  const cancel = () => { setSrc(null); setPixels(null); };
  const confirm = async () => {
    if (!pixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      const fd = new FormData();
      fd.append("file", blob, "logo.png");
      const { data } = await api.post("/coach/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.logo_url);
      toast.success("Logo updated");
      cancel();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't upload the logo.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="logo-uploader">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <PreviewTile label="On a light header" bg="var(--paper-white)" logo={value} />
        <PreviewTile label="On a dark header" bg="#0b3a30" logo={value} dark />
      </div>
      <label data-testid="logo-pick" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", border: "1px dashed var(--border-strong)", borderRadius: 10, cursor: "pointer", font: "var(--type-label)", color: "var(--text-body)" }}>
        <Upload size={16} /> {value ? "Replace logo" : "Upload logo"}
        <input data-testid="set-logo" type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
      </label>

      {src && (
        <div data-testid="logo-crop-modal" style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(10,20,16,0.6)", display: "grid", placeItems: "center", padding: 16 }}>
          <div style={{ background: "var(--surface-card, #fff)", borderRadius: 16, padding: 20, width: "min(520px,100%)" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ font: "var(--type-heading)", flex: 1 }}>Crop your logo</h3>
              <button data-testid="logo-crop-x" onClick={cancel} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-faint)" }}><X size={20} /></button>
            </div>
            <div style={{ position: "relative", width: "100%", height: 240, background: "#111", borderRadius: 10, overflow: "hidden" }}>
              <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} restrictPosition={false} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 6 }}>
                <AspectBtn active={aspect === 4} onClick={() => setAspect(4)} label="Wide" testid="logo-aspect-wide" />
                <AspectBtn active={aspect === 1} onClick={() => setAspect(1)} label="Square" testid="logo-aspect-square" />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 160 }}>
                <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>Zoom</span>
                <input data-testid="logo-zoom" type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ flex: 1, accentColor: accent }} />
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <Button variant="secondary" size="sm" data-testid="logo-crop-cancel" onClick={cancel}>Cancel</Button>
              <Button size="sm" data-testid="logo-crop-save" onClick={confirm} disabled={saving} iconLeft={<Check size={15} />}>{saving ? "Uploading…" : "Use this"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewTile({ label, bg, logo, dark }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ height: 56, minWidth: 180, padding: "0 16px", background: bg, borderRadius: 10, border: "1px solid var(--border-hairline)", display: "flex", alignItems: "center" }}>
        {logo
          ? <img src={mediaUrl(logo)} alt="logo preview" data-testid="logo-preview-img" style={{ height: 30, maxWidth: 150, objectFit: "contain" }} />
          : <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: dark ? "rgba(255,255,255,0.6)" : "var(--text-faint)" }}>No logo yet</span>}
      </div>
      <span style={{ font: "var(--type-body-sm)", fontSize: 11, color: "var(--text-faint)" }}>{label}</span>
    </div>
  );
}

function AspectBtn({ active, onClick, label, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + (active ? "var(--buktu-green)" : "var(--border-strong)"), background: active ? "var(--surface-brand-wash)" : "transparent", font: "var(--type-label)", fontSize: 13, cursor: "pointer", color: active ? "var(--buktu-green-deep)" : "var(--text-body)" }}>{label}</button>
  );
}
